<template>
  <div class="calendar">
    <div
      v-if="calendar_title"
      class="calendar_title">
      {{calendar_title}}
    </div>
    <div class="calendar-navigation">
      <year-input
        :value="selected_year"
        @changeYear="changeYear"/>
      <custom-checkbox
        :checked="show_full_year"
        @change="changeShowFullYear"
        name="show_full_year">
        <span>{{ $t('vacations.show_full_year') }}</span>
      </custom-checkbox>
      <month-select
        v-if="!show_full_year"
        :selected_month="selected_month"
        @changeMonth="changeMonth"
      />
      <default-button
        button_small
        :disabled="!(selected_year !== today_date.getFullYear() || (!show_full_year && selected_month !== today_date.getMonth()))"
        @click="setNewDate(today_date)">
        {{ $t('inputs.today') }}
      </default-button>
    </div>
    <div
      class="calendar-inner"
      ref="calendar"
      :class="{two_in_column: show_two_in_column}">
      <calendar-month
        v-for="month in get_show_months"
        :month_index="month_names.findIndex(el => el === month)"
        :month_name="month"
        :month_year="selected_year"
        :current_date="current_date"
        :show_year_in_month="show_year_in_month"
        :key="`calendar_${month}_${uniq_id}`"
        :selected_dates="selected_dates"
        :disabled_dates="disabled_dates"
        :background_texts_by_date="background_texts_by_date"
        :selectable_weekend="selectable_weekend"
        @selectedDay="selectedDay"
      />
    </div>

  </div>
</template>

<script>
import CalendarMonth from "./CalendarMonth";
import DefaultButton from "../DefaultButton";
import SearchInput from "../SearchInput";
import CustomCheckbox from "../form-parts/CustomCheckbox";
import {unlinkVueVariable} from "../../../helpers/global_vars";
import MonthSelect from "./MonthSelect.vue";
import YearInput from "./YearInput.vue";

const EVENT_COLORS = [
  '#B9A2FF', '#A0AEC0', '#718096', '#63171B',
  '#9C4221', '#D69E2E', '#975A16', '#276749',
  '#38A169', '#38B2AC', '#2C7A7B', '#234E52',
  '#63B3ED', '#3182CE', '#2C5282', '#B794F4',
  '#9F7AEA', '#805AD5', '#553C9A', '#97266D',
]

export const EVENT_UNIQ_CLASS =  "calendar_date_event"
export default {
  name: "Calendar",
  components: {
    YearInput,
    MonthSelect,
    CustomCheckbox,
    SearchInput,
    DefaultButton,
    CalendarMonth
  },
  props: {
    show_year_in_month: {
      type: Boolean,
      default: false
    },
    calendar_title: {
      default: ''
    },
    show_two_in_column: {
      type: Boolean,
      default: false
    },
    /** Value Props */
    is_multiple_choice: {
      type: Boolean,
      default: false
    },
    // is_range_choice: {// todo add prop
    //   type: Boolean,
    //   default: false
    // },
    selected_dates: {
      type: Array,
      default: () => []
    },
    calendar_date_outer: {
      type: [String, Number],
      default: ''
    },
    disabled_dates: {
      type: Array,
      default: () => []
    },
    background_texts_by_date: {
      type: Object,
      default: null
    },
    selectable_weekend: {
      type: Boolean,
      default: false
    },
    events: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    /** Props */
    uniq_id() {
      return this.ui_id
    },
    month_names() {
      return [
        'January', 'February', 'March', 'April', 'May', 'June', 'July',
        'August', 'September', 'October', 'November', 'December'
      ]
    },
    today_date() {
      return new Date
    },
    current_date() {
      return `${this.today_date.getFullYear()}-${this.today_date.getMonth()+1}-${this.today_date.getDate()}`
    },
    selected_year() {
      return this.selected_date.getFullYear()
    },
    selected_month() {
      return this.selected_date.getMonth()
    },
    get_show_months() {
      if (this.show_full_year) {
        return this.month_names
      }
      if (!this.show_two_in_column) {
        return [this.month_names[this.selected_month]]
      }
      let months_to_show_indexes = [this.selected_month]
      if (this.selected_month !== this.month_names.lengths) {
        months_to_show_indexes.push(this.selected_month + 1)
      }
      return this.month_names.filter((el, i) => months_to_show_indexes.includes(i))
    }
    /** End Props */
  },
  data() {
    return {
      selected_date: new Date,
      show_full_year: false,
      local_events: [],
    }
  },
  methods: {
    /** Props */
    changeYear(year) {
      this.$emit('update:calendar_date_outer', year)
      this.$emit('yearChange', year)
      this.setNewDateByProps({year: year, month: this.selected_month})
    },
    changeMonth(monthIndex) {
      this.setNewDate(new Date(this.selected_year, Number(monthIndex) + 1, 0))
    },
    setNewDateByProps({year, month, date = 1}) {
      this.setNewDate(new Date(year, month, date))
    },
    setNewDate(date) {
      if (!(date instanceof Date)) {
        return
      }
      this.selected_date = date
      this.rerenderEvent()
    },
    changeShowFullYear(e) {
      this.show_full_year = e.target.checked
      this.rerenderEvent()
    },
    /** End Props */

    selectedDay(date) {
      let deselect_date = false
      let emit_data = unlinkVueVariable(this.selected_dates)
      if (!this.is_multiple_choice) {
        emit_data = emit_data.includes(date)
          ? []
          : [date]
      } else {
        if (emit_data.includes(date)) {
          emit_data = emit_data.filter(el => el !== date)
          deselect_date = true
        } else {
          emit_data.push(date)
        }
      }
      this.$emit('selectedDays', {dates: emit_data, deselect_date: deselect_date})
    },

    /** Events */
    setEvents(list) {
      this.removeOldEvents()
      this.$set(
        this,
        'local_events',
        unlinkVueVariable(list).map(el => {
          el['position'] = null
          if (!el['uniq_color']) {
            el['uniq_color'] = EVENT_COLORS[Math.floor((Math.random()*EVENT_COLORS.length))]
          }
          return el
        })
      )
      this.updateEvents()
    },
    rerenderEvent() {
      this.$nextTick(() => {
        this.removeOldEvents()
        this.updateEvents()
      })
    },
    removeOldEvents() {
      this.$refs.calendar
        .querySelectorAll(`.${EVENT_UNIQ_CLASS}`)
        .forEach(el => {
          el.outerHTML = ''
        })
    },
    updateEvents() {
      this.local_events.forEach(event_group => {
        let title_added = false
        event_group.dates.forEach(date => {
          let node_date = this.$refs.calendar.querySelector(`.date-${date}`)
          if (!node_date) return
          let node_el = document.createElement('div')
          node_el.classList.add(EVENT_UNIQ_CLASS)
          if (event_group.date_start === date) {
            node_el.classList.add(`${EVENT_UNIQ_CLASS}-start`)
          } else if (event_group.date_end === date) {
            node_el.classList.add(`${EVENT_UNIQ_CLASS}-end`)
          }
          node_el.style.background = event_group.uniq_color
          if (!title_added) {
            node_el.classList.add(`${EVENT_UNIQ_CLASS}-title_added`)
            title_added = true
          }
          node_el.setAttribute('title', event_group.name)
          node_el.innerText = event_group.name
          node_date.append(node_el)
        })
      })
    }
    /** End events */
  },
  watch: {
    'events'() {
      this.setEvents(this.events)
    }
  }
}
</script>
